import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { FileDropzone } from 'src/shared/components';

import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Checkbox
} from 'src/shared/components/ui';
import {
  ButtonV2,
  Input,
  Textarea,
  Typography
} from 'src/shared/components/ui';

import {
  ServerError,
  serverFieldError
} from '../api/submitSupplierApplication';
import useSupplierApplication from '../hooks/useSupplierApplication';
import { COUNTRY_SELECT_ITEMS } from '../model/countries';
import {
  PAYMENT_METHODS,
  SupplierApplicationFormData,
  supplierApplicationSchema
} from '../model/schema';

// ─── Section wrapper ─────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div className="flex flex-col gap-6">
    <Typography
      variant="h3"
      weight="bold"
      className="text-primary-text pb-2 border-b border-gray-3"
    >
      {title}
    </Typography>
    {children}
  </div>
);

// ─── Success state ────────────────────────────────────────────────────────────

const SuccessState: React.FC = () => (
  <motion.div
    initial={{ y: 40, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ ease: 'easeInOut', duration: 0.8 }}
    className="flex flex-col items-center text-center gap-6 py-16"
  >
    <div className="size-20 rounded-full bg-green-50 flex items-center justify-center">
      <svg
        className="size-10 text-green-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <div className="flex flex-col gap-2">
      <Typography variant="h2" weight="bold">
        Application submitted!
      </Typography>
      <Typography variant="body-1" className="text-secondary-text max-w-md">
        Thank you for applying to become a Cavela supplier. Our team reviews all
        applications and will reach out if there is a potential fit.
      </Typography>
    </div>
  </motion.div>
);

// ─── Main form ────────────────────────────────────────────────────────────────

const SupplierApplicationForm: React.FC = () => {
  const mutation = useSupplierApplication();

  // File state managed outside react-hook-form (File objects aren't serialisable)
  const [catalogFiles, setCatalogFiles] = useState<File[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<{
    catalog?: string;
    photos?: string;
  }>({});

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors }
  } = useForm<SupplierApplicationFormData>({
    resolver: zodResolver(supplierApplicationSchema),
    defaultValues: {
      factory_size_sqm: null,
      number_of_employees: null,
      moq: null,
      accepted_payment_methods: []
    }
  });

  const validateFiles = (): boolean => {
    const errs: { catalog?: string; photos?: string } = {};

    if (catalogFiles.length === 0) {
      errs.catalog = 'Please upload at least one catalog or portfolio file.';
    }
    if (photoFiles.length === 0) {
      errs.photos = 'Please upload at least one factory or product photo.';
    }
    setFileErrors(errs);

    return Object.keys(errs).length === 0;
  };

  const onSubmit = (data: SupplierApplicationFormData) => {
    if (!validateFiles()) return;
    mutation.mutate(
      { formData: data, catalogFiles, photoFiles },
      {
        onError: (error) => {
          const fieldError = serverFieldError(error);

          if (fieldError) {
            setError(fieldError.field, { message: fieldError.message });
          }
        }
      }
    );
  };

  if (mutation.isSuccess) {
    return <SuccessState />;
  }

  // Errors about a single field are shown on that field instead.
  const serverError = serverFieldError(mutation.error)
    ? null
    : ((mutation.error as ServerError | null)?.response?.data?.error ?? null);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-10 mt-8"
    >
      {/* ── Factory Information ── */}
      <Section title="Factory Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Factory Name *"
            placeholder="Enter your company's registered name"
            errorMessage={errors.factory_name?.message}
            isInvalid={!!errors.factory_name}
            {...register('factory_name')}
          />
          <Input
            label="Year Established *"
            placeholder="e.g. 2005"
            type="number"
            errorMessage={errors.year_established?.message}
            isInvalid={!!errors.year_established}
            {...register('year_established', { valueAsNumber: true })}
          />
        </div>

        <div className="flex flex-col gap-4">
          <Input
            label="Address"
            placeholder="Street address"
            {...register('address')}
          />
          <Input
            label="Address Line 2 (Optional)"
            placeholder="Apartment, suite, floor, etc."
            {...register('address_line2')}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="City" placeholder="City" {...register('city')} />
            <Input
              label="State / Province"
              placeholder="State / Province"
              {...register('state')}
            />
            <Input
              label="ZIP / Postal Code"
              placeholder="Postal code"
              {...register('postal_code')}
            />
          </div>

          {/* Country — type-to-filter autocomplete */}
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Autocomplete
                label="Country *"
                placeholder="Search country…"
                defaultItems={COUNTRY_SELECT_ITEMS}
                selectedKey={field.value ?? null}
                onSelectionChange={(key) => field.onChange(key ?? '')}
                isInvalid={!!errors.country}
                errorMessage={errors.country?.message}
                labelPlacement="outside"
                size="lg"
                variant="bordered"
                classNames={{
                  base: 'w-full'
                }}
                inputProps={{
                  classNames: {
                    inputWrapper: [
                      'shadow-none',
                      '!bg-white border-1 border-gray-4',
                      'hover:!bg-white',
                      'focus-within:!bg-white focus-within:border-foreground',
                      'rounded-lg'
                    ],
                    label: '!text-secondary-text',
                    input: 'font-medium'
                  }
                }}
              >
                {(item) => (
                  <AutocompleteItem key={item.key}>
                    {item.label}
                  </AutocompleteItem>
                )}
              </Autocomplete>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Factory Size / Area (m²)"
            placeholder="Enter size in square metres (optional)"
            type="number"
            errorMessage={errors.factory_size_sqm?.message}
            isInvalid={!!errors.factory_size_sqm}
            {...register('factory_size_sqm', {
              setValueAs: (v) =>
                v === '' || v === null || v === undefined ? null : Number(v)
            })}
          />
          <Input
            label="Number of Employees"
            placeholder="Total number of employees (optional)"
            type="number"
            errorMessage={errors.number_of_employees?.message}
            isInvalid={!!errors.number_of_employees}
            {...register('number_of_employees', {
              setValueAs: (v) =>
                v === '' || v === null || v === undefined ? null : Number(v)
            })}
          />
        </div>
      </Section>

      {/* ── Production & Capabilities ── */}
      <Section title="Production & Capabilities">
        <Input
          label="Production Capacity (monthly / yearly) *"
          placeholder="e.g. 100,000 units / month"
          errorMessage={errors.production_capacity?.message}
          isInvalid={!!errors.production_capacity}
          {...register('production_capacity')}
        />
        <Input
          label="Main Product Categories *"
          placeholder="List your company's main product categories"
          errorMessage={errors.main_product_categories?.message}
          isInvalid={!!errors.main_product_categories}
          {...register('main_product_categories')}
        />
        <Textarea
          label="Export Percentage / Main Export Markets *"
          placeholder={'70% USA\n20% Australia\n10% Europe'}
          minRows={3}
          errorMessage={errors.export_markets?.message}
          isInvalid={!!errors.export_markets}
          {...register('export_markets')}
        />
        <Input
          label="Materials Specialized In *"
          placeholder="e.g. cotton, leather, stainless steel"
          errorMessage={errors.materials_specialized_in?.message}
          isInvalid={!!errors.materials_specialized_in}
          {...register('materials_specialized_in')}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Input
            label="Minimum Order Quantity (MOQ)"
            placeholder="Enter MOQ (optional)"
            type="number"
            errorMessage={errors.moq?.message}
            isInvalid={!!errors.moq}
            {...register('moq', {
              setValueAs: (v) =>
                v === '' || v === null || v === undefined ? null : Number(v)
            })}
          />
          <Input
            label="Sample Lead Time"
            placeholder="Estimated sample lead time"
            {...register('sample_lead_time')}
          />
          <Input
            label="Bulk Production Lead Time"
            placeholder="Estimated bulk production lead time"
            {...register('bulk_production_lead_time')}
          />
        </div>
        <Input
          label="Certifications / Compliance *"
          placeholder="ISO, BSCI, SEDEX, OEKO-TEX, GOTS, etc."
          errorMessage={errors.certifications?.message}
          isInvalid={!!errors.certifications}
          {...register('certifications')}
        />
        <Input
          label="Preferred Payment Terms"
          placeholder="Specify your preferred payment terms (optional)"
          {...register('preferred_payment_terms')}
        />

        <Controller
          name="accepted_payment_methods"
          control={control}
          defaultValue={[]}
          render={({ field }) => (
            <div className="flex flex-col gap-2">
              <Typography variant="body-2" className="text-secondary-text">
                Accepted Payment Methods *
              </Typography>
              <div className="flex flex-col gap-3">
                {PAYMENT_METHODS.map((method) => (
                  <Checkbox
                    key={method}
                    isSelected={field.value?.includes(method) ?? false}
                    onValueChange={(checked) => {
                      const current = field.value ?? [];

                      field.onChange(
                        checked
                          ? [...current, method]
                          : current.filter((m) => m !== method)
                      );
                    }}
                  >
                    {method}
                  </Checkbox>
                ))}
              </div>
              {errors.accepted_payment_methods && (
                <Typography variant="body-2" className="text-danger">
                  {errors.accepted_payment_methods.message}
                </Typography>
              )}
            </div>
          )}
        />
      </Section>

      {/* ── Contact Information — 2-column layout ── */}
      <Section title="Contact Information">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* ─ Primary contact (left) ─ */}
          <div className="flex flex-col gap-4">
            <Typography
              variant="body-1"
              weight="bold"
              className="text-primary-text"
            >
              Primary Contact
            </Typography>
            <Input
              label="Name *"
              placeholder="Name of primary contact person"
              errorMessage={errors.primary_contact_name?.message}
              isInvalid={!!errors.primary_contact_name}
              {...register('primary_contact_name')}
            />
            <Input
              label="Role / Designation *"
              placeholder="Role or job title"
              errorMessage={errors.primary_contact_role?.message}
              isInvalid={!!errors.primary_contact_role}
              {...register('primary_contact_role')}
            />
            <Input
              label="Email Address *"
              placeholder="primary@company.com"
              type="email"
              errorMessage={errors.email?.message}
              isInvalid={!!errors.email}
              {...register('email')}
            />
            <Input
              label="WhatsApp Number"
              placeholder="+86 138 0000 0000"
              type="tel"
              errorMessage={errors.whatsapp_number?.message}
              isInvalid={!!errors.whatsapp_number}
              {...register('whatsapp_number')}
            />
            <Input
              label="WeChat ID"
              placeholder="If applicable, provide WeChat ID"
              {...register('wechat_id')}
            />
          </div>

          {/* ─ Secondary contact (right) ─ */}
          <div className="flex flex-col gap-4">
            <Typography
              variant="body-1"
              weight="bold"
              className="text-primary-text"
            >
              Secondary Contact
            </Typography>
            <Input
              label="Name"
              placeholder="Name of secondary contact (optional)"
              {...register('secondary_contact_name')}
            />
            <Input
              label="Role / Designation"
              placeholder="Role or job title (optional)"
              {...register('secondary_contact_role')}
            />
            <Input
              label="Email Address"
              placeholder="secondary@company.com (optional)"
              type="email"
              errorMessage={errors.additional_email?.message}
              isInvalid={!!errors.additional_email}
              {...register('additional_email')}
            />
            <Input
              label="WhatsApp Number"
              placeholder="+86 138 0000 0001 (optional)"
              type="tel"
              {...register('additional_whatsapp_number')}
            />
            <Input
              label="WeChat ID"
              placeholder="Optional"
              {...register('additional_wechat_id')}
            />
          </div>
        </div>
      </Section>

      {/* ── Catalog & Portfolio ── */}
      <Section title="Catalog & Portfolio">
        <Input
          label="Website or Company Profile Link *"
          placeholder="www.yourcompany.com"
          errorMessage={errors.website?.message}
          isInvalid={!!errors.website}
          {...register('website')}
        />

        <FileDropzone
          label="Product Catalog or Portfolio"
          files={catalogFiles}
          onChange={(files) => {
            setCatalogFiles(files);
            if (files.length > 0) {
              setFileErrors((prev) => ({ ...prev, catalog: undefined }));
            }
          }}
          error={fileErrors.catalog}
          required
        />

        <Textarea
          label="Previous Clients *"
          placeholder="This information will not be shared with brands — it is for internal purposes only. You may be asked to submit proof; dishonesty will affect inclusion in platform."
          minRows={3}
          errorMessage={errors.previous_clients?.message}
          isInvalid={!!errors.previous_clients}
          {...register('previous_clients')}
        />

        <FileDropzone
          label="Factory & Product Photos"
          files={photoFiles}
          onChange={(files) => {
            setPhotoFiles(files);
            if (files.length > 0) {
              setFileErrors((prev) => ({ ...prev, photos: undefined }));
            }
          }}
          error={fileErrors.photos}
          required
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      </Section>

      {serverError && <Alert hideIcon color="danger" title={serverError} />}

      {/* Validation error summary — shows all blocking errors near the submit button */}
      {Object.keys(errors).length > 0 && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 p-4 flex flex-col gap-2">
          <Typography variant="body-2" weight="bold" className="text-danger">
            Please fix the following before submitting:
          </Typography>
          <ul className="flex flex-col gap-1 list-disc list-inside">
            {(
              Object.entries(errors) as [
                string,
                { message?: string } | { message?: string }[]
              ][]
            ).map(([field, error]) => {
              const message = Array.isArray(error)
                ? (error[0]?.message ?? field)
                : ((error as { message?: string })?.message ?? field);

              return (
                <li key={field}>
                  <Typography variant="body-2" className="text-danger inline">
                    <span className="font-medium">{field}:</span> {message}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="pb-10">
        <ButtonV2
          type="submit"
          isLoading={mutation.isPending}
          isDisabled={mutation.isPending}
          size="xl"
          className="w-full md:w-auto"
        >
          Submit Application
        </ButtonV2>
      </div>
    </form>
  );
};

export default SupplierApplicationForm;
